import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import logger from "../utils/logger";

const AffAuthContext = createContext(null);
const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api`;

// NOTE: See AuthContext.jsx for rationale on localStorage token storage.
function getToken() { return localStorage.getItem("flux_aff_token"); }
function setToken(t) { localStorage.setItem("flux_aff_token", t); }
function clearToken() { localStorage.removeItem("flux_aff_token"); }
function getHeaders() { const t = getToken(); return t ? { Authorization: `Bearer ${t}` } : {}; }

export { getHeaders as getAffHeaders, API as AFF_API };

export function AffAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/affiliate/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      setUser(res.data);
    } catch (err) {
      logger.error("Aff auth check failed:", err);
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((userData, sessionToken) => {
    setToken(sessionToken);
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/affiliate/auth/logout`, {}, { headers: getHeaders() });
    } catch (err) {
      logger.error("Aff logout error:", err);
    }
    clearToken();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, logout, login, checkAuth }),
    [user, loading, logout, login, checkAuth]
  );

  return (
    <AffAuthContext.Provider value={value}>
      {children}
    </AffAuthContext.Provider>
  );
}

export function useAffAuth() {
  const ctx = useContext(AffAuthContext);
  if (!ctx) throw new Error("useAffAuth must be used within AffAuthProvider");
  return ctx;
}
